import {
    useEffect,
    Fragment,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    Linking,
    Image,
    Modal,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import CustomStatusChip from "./components/customStatusChip";
import {
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    PageContainer,
    TextInput,
    RowCard,
    Sticker,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../../../navigation/type";
import type {
    GetEventForAdminResponseType,
    EventDetailType
} from "../../../../../services/restAPI/actions/getEventForAdmin/type";
import type {
    GetSubEventForAdminResponseType,
    SubEventDetailType
} from "../../../../../services/restAPI/actions/getSubEventForAdmin/type";
import type {
    ActionsType
} from "../../../../../services/restAPI/types";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    ArrowLeftIcon,
    SearchIcon,
    PenIcon,
    XIcon
} from "lucide-react-native";
import moment from "moment";
import RESTServiceContext from "../../../../../services/restAPI/context";
import SmartCoverImage from "../../../../../components/smartCoverImage";

const SubEventDetail = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute();

    const params = route.params as { eventID: string; subEventID: string };
    const eventID = params?.eventID;
    const subEventID = params?.subEventID;

    const [
        subEventData,
        setSubEventData
    ] = useState<SubEventDetailType | null>(null);

    const [
        eventData,
        setEventData
    ] = useState<EventDetailType | null>(null);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isLightboxVisible,
        setIsLightboxVisible
    ] = useState<boolean>(false);

    const [
        expandedRowIDs,
        setExpandedRowIDs
    ] = useState<string[]>([]);

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);
    const [
        dialogTitle,
        setDialogTitle
    ] = useState<string>("");
    const [
        dialogAction,
        setDialogAction
    ] = useState<(() => void) | null>(null);

    const [
        deletingPhotoUrl,
        setDeletingPhotoUrl
    ] = useState<string | null>(null);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fetchSubEventDetail = async () => {
        if (!subEventID || !eventID) {
            setIsLoading(false);
            return;
        }
        try {
            const [
                subEventRes,
                eventRes
            ] = await Promise.all([
                RESTServiceContext.action("getSubEventForAdmin", {
                    eventSubID: subEventID,
                    eventID: eventID
                }),
                RESTServiceContext.action("getEventForAdmin", {
                    eventID: eventID
                })
            ]);

            const typedSubEventRes = subEventRes as unknown as GetSubEventForAdminResponseType;
            const typedEventRes = eventRes as unknown as GetEventForAdminResponseType;

            if (typedSubEventRes.payload) {
                setSubEventData(typedSubEventRes.payload);
            }
            if (typedEventRes.payload) {
                setEventData(typedEventRes.payload);
            }
        } catch (error) {
            console.error("Error fetching detail:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAction = async (actionName: ActionsType, additionalParams: Record<string, unknown> = {}) => {
        try {
            await RESTServiceContext.action(actionName, {
                eventID: eventID as string,
                ...additionalParams
            });
            NCoreUIKitToast.open({
                title: localize("global-success") || "Başarılı",
                type: "success"
            });
            fetchSubEventDetail();
        } catch {
            NCoreUIKitToast.open({
                title: localize("global-error") || "Hata oluştu",
                type: "danger"
            });
        }
    };

    const confirmAction = (actionName: string, actionCallback: () => void) => {
        setDialogTitle(actionName);
        setDialogAction(() => actionCallback);
        setIsDialogVisible(true);
    };

    const handleStartEvent = () => confirmAction(
        localize("events-detail-start-event") || "Etkinliği Başlat",
        () => handleAction("startEvent", {
            targetDate: new Date().toISOString(),
            subEventID
        })
    );

    const handleFinishEvent = () => confirmAction(
        localize("events-detail-finish-event") || "Etkinliği Bitir",
        () => handleAction("finishEvent", {
            targetDate: new Date().toISOString(),
            subEventID
        })
    );

    const handleStartApplication = () => confirmAction(
        localize("events-detail-start-app") || "Başvuruyu Başlat",
        () => handleAction("startEventApplication", {
            targetDate: new Date().toISOString(),
            subEventID
        })
    );

    const handleEndApplication = () => confirmAction(
        localize("events-detail-end-app") || "Başvuruyu Bitir",
        () => handleAction("endEventApplication", {
            targetDate: new Date().toISOString(),
            subEventID
        })
    );

    const handleCancelEvent = () => confirmAction(
        localize("events-detail-cancel-event") || "İptal Et",
        () => handleAction("cancelSubEvent", {
            eventSubID: subEventID
        })
    );

    useEffect(() => {
        fetchSubEventDetail();
    }, [
        subEventID,
        eventID
    ]);

    const toggleRow = (id: string) => {
        setExpandedRowIDs(prev => prev.includes(id) ? prev.filter(r => r !== id) : [
            ...prev,
            id
        ]);
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                icon={() => <ArrowLeftIcon
                    color={colors.project.brandWhite}
                    size={24}
                />}
                customBorderColor="secondaryButton"
                onPress={() => navigation.goBack()}
                customColor="secondaryButton"
                spreadBehaviour="free"
                variant="filled"
            />
            <Text
                customColor={colors.content.text.high}
                variant="titleLargeSize"
                style={{
                    flex: 1
                }}
            >
                {localize("events-detail-title")}
            </Text>
            <Button
                title={localize("sub-event-detail-edit")}
                onPress={() => {
                    navigation.navigate("SubEventEdit", {
                        subEventID: subEventID as string,
                        eventID: eventID as string
                    });
                }}
                icon={({
                    color,
                    size
                }) => <PenIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                customBorderColor="secondaryButton"
                customColor="secondaryButton"
                variant="filled"
            />
        </View>;
    };

    const handleDeletePhoto = (photoUrl: string) => {
        confirmAction(localize("events-detail-delete-photo") || "Fotoğrafı Sil", async () => {
            let photoKey = photoUrl;
            const searchKey = "/events/subEventPhotos/";
            const index = photoUrl.indexOf(searchKey);
            if (index !== -1) {
                photoKey = photoUrl.substring(index);
            }

            try {
                setDeletingPhotoUrl(photoUrl);
                await RESTServiceContext.action("removeSubEventPhoto", {
                    eventSubID: subEventID as string,
                    eventID: eventID as string,
                    photoKey: photoKey
                });

                if (subEventData && subEventData.photos) {
                    setSubEventData({
                        ...subEventData,
                        photos: subEventData.photos.filter(p => p !== photoUrl)
                    });
                }
            } catch (err) {
                console.log(err);
            } finally {
                setDeletingPhotoUrl(null);
            }
        });
    };

    const renderPhotos = () => {
        if (!subEventData?.photos || subEventData.photos.length === 0) {
            return null;
        }

        return <View
            style={{
                paddingHorizontal: spaces.spacingMd,
                marginTop: spaces.spacingLg
            }}
        >
            <Text
                customColor={colors.content.text.high}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                variant="titleMediumSize"
            >
                {localize("events-detail-photos")}
            </Text>
            <View
                style={[
                    stylesheet.photoGrid,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {subEventData.photos.map((photoUrl: string, index: number) => <View
                    style={[
                        stylesheet.photoContainer,
                        stylesheet.photoContainerInner,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.md
                        }
                    ]}
                    key={`photo-${index}`}
                >
                    <Image
                        style={stylesheet.photoImage}
                        resizeMode="cover"
                        source={{
                            uri: photoUrl
                        }}
                    />
                    {
                        deletingPhotoUrl === photoUrl ?
                            <View
                                style={[
                                    stylesheet.photoLoadingOverlay,
                                    {
                                        backgroundColor: colors.content.container.overlay,
                                        borderRadius: radiuses.md
                                    }
                                ]}
                            >
                                <ActivityIndicator
                                    color={colors.project.brandWhite}
                                    size="small"
                                />
                            </View>
                        :
                            <TouchableOpacity
                                style={[
                                    stylesheet.photoDeleteButton,
                                    {
                                        backgroundColor: colors.content.container.danger,
                                        borderRadius: radiuses.full
                                    }
                                ]}
                                onPress={() => handleDeletePhoto(photoUrl)}
                            >
                                <Text
                                    customColor={colors.project.brandWhite}
                                    variant="bodySmallSize"
                                >
                                    X
                                </Text>
                            </TouchableOpacity>
                    }
                </View>)}
            </View>
        </View>;
    };

    const renderDashboardInfo = () => {
        if (!subEventData) return null;

        return <View
            style={[
                stylesheet.dashboardCard,
                {
                    backgroundColor: colors.content.container.subtle,
                    marginTop: spaces.spacingMd,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg
                }
            ]}
        >
            <View
                style={stylesheet.dashboardCardTitleContainer}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleMediumSize"
                >
                    {localize("events-detail-status-info")}
                </Text>
                <CustomStatusChip
                    status={subEventData.status}
                />
            </View>
            <View
                style={{
                    marginTop: spaces.spacingMd,
                    gap: spaces.spacingSm
                }}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {`${localize("events-detail-type")}: ${subEventData.type === "application" ? localize("events-filter-application") : localize("events-filter-standard")}`}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {`${localize("events-detail-start-date")}: ${subEventData.startDate ? moment(subEventData.startDate).format("DD.MM.YYYY HH:mm") : "-"}`}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {`${localize("events-detail-end-date")}: ${subEventData.endDate ? moment(subEventData.endDate).format("DD.MM.YYYY HH:mm") : "-"}`}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {`${localize("events-location")}: ${subEventData.eventLocation || "-"}`}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {`${localize("events-entrance-code")}: ${subEventData.entranceCode || "-"}`}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {`${localize("events-start-notification-title")}: ${subEventData.startNotificationTitle || "-"}`}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {`${localize("events-start-notification-desc")}: ${subEventData.startNotificationDescription || "-"}`}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {`${localize("events-app-start-notification-title")}: ${subEventData.applicationStartNotificationTitle || "-"}`}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {`${localize("events-app-end-notification-title")}: ${subEventData.applicationEndNotificationTitle || "-"}`}
                </Text>
            </View>
            {subEventData.status !== "finished" && (
                <View
                    style={{
                        borderTopColor: colors.content.border.subtle,
                        borderTopWidth: borders.seperator,
                        paddingTop: spaces.spacingLg,
                        marginTop: spaces.spacingLg,
                        gap: spaces.spacingMd,
                        flexDirection: "row",
                        flexWrap: "wrap"
                    }}
                >
                    <Button
                        isDisabled={subEventData.status === "continue" || (subEventData.type === "application" && subEventData.status === "announcement")}
                        title={localize("events-detail-start-event") || "Etkinliği Başlat"}
                        onPress={handleStartEvent}
                        variant="filled"
                        type="success"
                        size="small"
                    />
                    <Button
                        title={localize("events-detail-finish-event") || "Etkinliği Bitir"}
                        isDisabled={subEventData.status !== "continue"}
                        onPress={handleFinishEvent}
                        variant="filled"
                        size="small"
                        type="info"
                    />
                    {
                        subEventData.type === "application" ?
                            <Fragment>
                                <Button
                                    title={localize("events-detail-start-app") || "Başvuruyu Başlat"}
                                    isDisabled={subEventData.status !== "announcement"}
                                    onPress={handleStartApplication}
                                    variant="filled"
                                    type="warning"
                                    size="small"
                                />
                                <Button
                                    title={localize("events-detail-end-app") || "Başvuruyu Bitir"}
                                    isDisabled={subEventData.status !== "application"}
                                    onPress={handleEndApplication}
                                    variant="filled"
                                    type="warning"
                                    size="small"
                                />
                            </Fragment>
                        :
                            null
                    }
                    <Button
                        title={localize("events-detail-cancel-event") || "İptal Et"}
                        onPress={handleCancelEvent}
                        isDisabled={false}
                        variant="filled"
                        type="danger"
                        size="small"
                    />
                </View>)}
        </View>;
    };

    const filteredApplications = subEventData?.applications?.filter((app) => {
        if(!searchQuery) return true;

        const query = searchQuery.toLowerCase();

        const phoneMatch = (app.phone || "").toLowerCase().includes(query);
        const nameMatch = (app.userDetails?.name || "").toLowerCase().includes(query);
        const surnameMatch = (app.userDetails?.surname || "").toLowerCase().includes(query);
        const answersMatch = (app.answers || []).some(ans => (ans.value || "").toLowerCase().includes(query));

        return phoneMatch || nameMatch || surnameMatch || answersMatch;
    }) || [];

    const renderFormAnswers = () => {
        if (!subEventData || subEventData.type !== "application") return null;

        return <View
            style={{
                marginTop: spaces.spacingXl
            }}
        >
            <Text
                customColor={colors.content.text.high}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                variant="titleMediumSize"
            >
                {localize("events-form-answers")}
            </Text>
            <TextInput
                placeholder={localize("forms-list-search-placeholder") || "Ara..."}
                customColor={colors.project.brandWhite}
                icon={({
                    color,
                    size
                }) => <SearchIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                onChangeText={setSearchQuery}
                spreadBehaviour="stretch"
                value={searchQuery}
            />
            {
                !filteredApplications || filteredApplications.length === 0 ?
                    <View
                        style={{
                            backgroundColor: colors.project.brandWhite,
                            borderColor: colors.project.brandBorder,
                            borderWidth: borders.seperator,
                            marginTop: spaces.spacingMd,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingLg,
                            alignItems: "center"
                        }}
                    >
                        <Text
                            customColor={colors.content.text.mid}
                            variant="bodyMediumSize"
                        >
                            {localize("events-no-form-answers")}
                        </Text>
                    </View>
                :
                    <View
                        style={{
                            marginTop: spaces.spacingMd,
                            gap: spaces.spacingMd
                        }}
                    >
                        {
                            filteredApplications.map((app, i) => {
                                const isExpanded = expandedRowIDs.includes(app.applicationID);
                                const nameSurname = `${app.userDetails?.name || "-"} ${app.userDetails?.surname || ""}`;
                                const phone = app.phone || app.userDetails?.phone || "-";
                                const dateStr = moment(app.createdAt).format("DD.MM.YYYY HH:mm");
                                const statusMap: Record<string, string> = {
                                    rejected: "Reddedildi",
                                    approved: "Onaylandı",
                                    pending: "Bekliyor",
                                    applied: "Başvuru",
                                    attended: "Katıldı"
                                };
                                const statusStr = statusMap[app.status] || app.status;

                                return <View
                                    style={{
                                        backgroundColor: colors.project.brandWhite,
                                        borderColor: colors.project.brandBorder,
                                        borderWidth: borders.seperator,
                                        marginBottom: spaces.spacingMd,
                                        borderRadius: radiuses.md,
                                        overflow: "hidden"
                                    }}
                                    key={app.applicationID || `app_${i}`}
                                >
                                    <RowCard
                                        onPress={() => toggleRow(app.applicationID)}
                                        rightIcon={({
                                            color,
                                            size
                                        }) => isExpanded ? <ChevronUpIcon
                                            color={colors.content.icon[color]}
                                            size={size}
                                        /> : <ChevronDownIcon
                                            color={colors.content.icon[color]}
                                            size={size}
                                        />}
                                        rightSubTitleVariant="bodySmallSize"
                                        rightTitleVariant="bodyMediumSize"
                                        subTitleVariant="bodySmallSize"
                                        titleVariant="titleMediumSize"
                                        rightSubTitleColor="low"
                                        rightSubTitle={dateStr}
                                        rightTitleColor="mid"
                                        subTitle={statusStr}
                                        subTitleColor="low"
                                        title={nameSurname}
                                        rightTitle={phone}
                                        titleColor="high"
                                        iconSize={20}
                                    />
                                    {
                                        isExpanded ?
                                            <View
                                                style={{
                                                    borderTopColor: colors.project.brandBorder,
                                                    borderTopWidth: borders.seperator,
                                                    padding: spaces.spacingMd,
                                                    gap: spaces.spacingMd
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        marginBottom: spaces.spacingXs
                                                    }}
                                                    variant="labelMediumSize"
                                                >
                                                    {localize("events-answers")}:
                                                </Text>
                                                {
                                                    (app.answers || []).map((ans, idx) => <View
                                                        style={{
                                                            marginBottom: spaces.spacingSm
                                                        }}
                                                        key={idx}
                                                    >
                                                        <Text
                                                            customColor={colors.content.text.mid}
                                                            variant="labelMediumSize"
                                                        >
                                                            {ans.title}
                                                        </Text>
                                                        {
                                                            ans.type === "photo" && typeof ans.value === "string" && ans.value.length > 0 ?
                                                                <TouchableOpacity
                                                                    onPress={() => Linking.openURL(ans.value as string)}
                                                                >
                                                                    <Image
                                                                        style={[
                                                                            stylesheet.previewImage,
                                                                            {
                                                                                marginTop: spaces.spacingXs,
                                                                                borderRadius: radiuses.md
                                                                            }
                                                                        ]}
                                                                        source={{
                                                                            uri: ans.value as string
                                                                        }}
                                                                        resizeMode="cover"
                                                                    />
                                                                </TouchableOpacity>
                                                            :
                                                                ans.type === "yes_no" ?
                                                                    <View
                                                                        style={{
                                                                            marginTop: spaces.spacingXs,
                                                                            alignItems: "flex-start"
                                                                        }}
                                                                    >
                                                                        <Sticker
                                                                            title={ans.value === "yes" || ans.value === "true" ? (localize("forms-create-confirm-yes") || "Evet") : (localize("forms-create-confirm-no") || "Hayır")}
                                                                            type={ans.value === "yes" || ans.value === "true" ? "success" : "danger"}
                                                                            spreadBehaviour="free"
                                                                            size="small"
                                                                        />
                                                                    </View>
                                                                :
                                                                    <Text
                                                                        customColor={colors.content.text.high}
                                                                        variant="bodyMediumSize"
                                                                    >
                                                                        {Array.isArray(ans.value) ? ans.value.join(", ") : (ans.value || "-")}
                                                                    </Text>
                                                        }
                                                    </View>)
                                                }
                                            </View>
                                        :
                                            null
                                    }
                                </View>;
                            })
                        }
                    </View>
            }
        </View>;
    };

    const renderCoverAndLogo = () => {
        if (!eventData?.coverPhotoPath && !eventData?.logoPath) return null;

        return <View
            style={[
                stylesheet.coverAndLogoContainer,
                {
                    marginBottom: spaces.spacingLg,
                    borderRadius: radiuses.md
                }
            ]}
        >
            {
                eventData.coverPhotoPath ?
                    <TouchableOpacity
                        onPress={() => setIsLightboxVisible(true)}
                        activeOpacity={0.8}
                    >
                        <SmartCoverImage
                            containerStyle={stylesheet.coverImage}
                            url={eventData.coverPhotoPath}
                        />
                    </TouchableOpacity>
                :
                    <View
                        style={[
                            stylesheet.coverPlaceholder,
                            {
                                backgroundColor: colors.content.container.subtle
                            }
                        ]}
                    />
            }
            {
                eventData.logoPath ?
                    <Image
                        style={[
                            stylesheet.logoImage,
                            {
                                backgroundColor: colors.project.brandWhite,
                                borderColor: colors.project.brandWhite,
                                borderWidth: borders.seperator * 2,
                                borderRadius: radiuses.full,
                                bottom: spaces.spacingMd,
                                left: spaces.spacingMd
                            }
                        ]}
                        source={{
                            uri: eventData.logoPath
                        }}
                    />
                :
                    null
            }
        </View>;
    };

    if (isLoading) {
        return <View
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                color={colors.content.icon.mid}
                size="large"
            />
        </View>;
    }

    return <PageContainer
        scrollViewStyle={stylesheet.container}
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            {renderCoverAndLogo()}
            {renderHeader()}
            {renderDashboardInfo()}
            {renderFormAnswers()}
            {renderPhotos()}
        </View>
        <Dialog
            onOverlayPress={() => setIsDialogVisible(false)}
            primaryButtonProps={{
                title: localize("members-form-dialog-yes"),
                onPress: () => {
                    if (dialogAction) {
                        dialogAction();
                    }
                    setIsDialogVisible(false);
                }
            }}
            secondaryButtonProps={{
                title: localize("members-form-dialog-no"),
                onPress: () => setIsDialogVisible(false)
            }}
            onClosed={() => setIsDialogVisible(false)}
            isVisible={isDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {dialogTitle || localize("members-form-dialog-content")}
                </Text>
            </View>
        </Dialog>
        <Modal
            onRequestClose={() => setIsLightboxVisible(false)}
            visible={isLightboxVisible}
            animationType="fade"
            transparent={true}
        >
            <View
                style={[
                    stylesheet.modalOverlay,
                    {
                        backgroundColor: colors.content.container.overlay
                    }
                ]}
            >
                <TouchableOpacity
                    onPress={() => setIsLightboxVisible(false)}
                    style={stylesheet.modalCloseButton}
                >
                    <XIcon
                        color={colors.project.brandWhite}
                        size={32}
                    />
                </TouchableOpacity>
                <Image
                    source={{
                        uri: eventData?.coverPhotoPath
                    }}
                    style={stylesheet.lightboxImage}
                    resizeMode="contain"
                />
            </View>
        </Modal>
    </PageContainer>;
};

export default SubEventDetail;
